import React from 'react';
import _ from 'lodash';
import * as T from 'prop-types';
import { Form, Field } from 'react-final-form';
import TransparentBackgroundOverlay from '../../commons/TransparentBackgroundOverlay';
import { getDisclaimerChecksByCountry } from '../../commons/CountryUtils';
import { buttonCss } from '../../commons/form-fields/FormFieldsStyling';

const disclaimerCheckTextCss = 'mb-4';

// Helper to build consistent field names
const getDisclaimerCheckFieldName = (checkName) =>
    `application.disclaimerChecks.${checkName}.confirmed`;

// Check if the disclaimer checks popup should be displayed
const shouldShowPopup = (initialValues, checks) => {
    // If there are no checks, don't show the popup.
    if (_.isEmpty(checks)) return false;

    // In case confirmation is pending for any of the checks, show the popup.
    if (!initialValues || _.isEmpty(initialValues)) return true;

    return _.some(checks, (check) => {
        const fieldPath = getDisclaimerCheckFieldName(check?.name);
        return !_.get(initialValues, fieldPath);
    });
};

const DisclaimerChecksPopUp = ({
    initialValues,
    consultantCountry,
    autoSaveCallback,
}) => {
    const disclaimerChecks = getDisclaimerChecksByCountry(consultantCountry);

    if (!shouldShowPopup(initialValues, disclaimerChecks)) {
        return <div />;
    }

    return (
        <Form
            onSubmit={(values) => {
                autoSaveCallback(values, {});
            }}
            initialValues={initialValues}
            render={({ handleSubmit, form }) => (
                <TransparentBackgroundOverlay
                    header="Confirmation"
                    hideCloseButton
                    zIndex={30}
                    onClose={_.noop}
                >
                    <div className="px-4 py-4 border border-gray-200">
                        {_.map(disclaimerChecks, (disclaimerCheck) => (
                            <>
                                {/* Disclaimer text */}
                                <div
                                    className={disclaimerCheckTextCss}
                                    dangerouslySetInnerHTML={{
                                        __html: disclaimerCheck?.text,
                                    }}
                                    data-testid={`disclaimer-check-${disclaimerCheck?.name}`}
                                />
                                {/* Hidden field to store confirmation */}
                                <Field
                                    name={getDisclaimerCheckFieldName(
                                        disclaimerCheck?.name
                                    )}
                                    render={() => <div />}
                                />
                            </>
                        ))}

                        {/* Single button-click to confirm all checks at once */}
                        <div className="mt-6 flex justify-center">
                            <button
                                className={buttonCss}
                                onClick={() => {
                                    // Set all fields to true on confirmation, and then submit the form
                                    _.forEach(disclaimerChecks, (check) => {
                                        form.change(
                                            getDisclaimerCheckFieldName(
                                                check?.name
                                            ),
                                            true
                                        );
                                    });
                                    handleSubmit();
                                }}
                                autoFocus
                                type="button"
                                data-testid="disclaimer-check-confirm-button"
                            >
                                I Confirm
                            </button>
                        </div>
                    </div>
                </TransparentBackgroundOverlay>
            )}
        />
    );
};

DisclaimerChecksPopUp.propTypes = {
    initialValues: T.object,
    consultantCountry: T.string.isRequired,
    autoSaveCallback: T.func.isRequired,
};

export default DisclaimerChecksPopUp;
